/**
 * Attack Surface panel — subdomains, email security posture, DNS grades.
 * Shown only when pipeline.company_exposure is present (COMPANY seeds).
 */
import { useState } from 'react';
import { ShieldAlert, Globe, Lock, Mail, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import type { CompanyAttackSurface, EmailSecurityPosture, SubdomainEntry } from '@/types/profiler';

// ── colour helpers ─────────────────────────────────────────────────────────────

const GRADE_COLOR: Record<string, string> = {
  A: '#00C853',
  B: '#69B578',
  C: '#F0B429',
  D: '#FF6B1A',
  F: '#FF1F1F',
};

function gradeColor(g?: string) {
  return GRADE_COLOR[g ?? 'F'] ?? '#5A7A9A';
}

function gradeLabel(g?: string): string {
  const m: Record<string, string> = {
    A: 'Excellent',
    B: 'Good',
    C: 'Fair',
    D: 'Weak',
    F: 'Critical',
  };
  return m[g ?? 'F'] ?? 'Unknown';
}

// ── sub-components ─────────────────────────────────────────────────────────────

function PostureRow({
  ok, label, detail,
}: { ok: boolean; label: string; detail?: string }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '7px 0',
        borderBottom: '1px solid #182030',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{
            width: 6, height: 6, borderRadius: '50%',
            background: ok ? '#00C853' : '#FF1F1F',
            flexShrink: 0,
          }}
        />
        <span style={{ color: '#DCE8F5', fontSize: 10 }}>{label}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {detail && (
          <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace' }}>{detail}</span>
        )}
        <span
          style={{
            background: ok ? '#00C85318' : '#FF1F1F18',
            color: ok ? '#00C853' : '#FF1F1F',
            border: `1px solid ${ok ? '#00C85330' : '#FF1F1F30'}`,
            fontSize: 8,
            fontFamily: 'monospace',
            fontWeight: 700,
            padding: '1px 6px',
            borderRadius: 99,
          }}
        >
          {ok ? 'PASS' : 'FAIL'}
        </span>
      </div>
    </div>
  );
}

function EmailPostureCard({ posture }: { posture: EmailSecurityPosture }) {
  const gc = gradeColor(posture.grade);
  return (
    <div
      style={{
        background: '#0C1118',
        border: `1px solid ${posture.grade === 'F' || posture.grade === 'D' ? '#FF1F1F30' : '#182030'}`,
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      {/* Grade header */}
      <div
        style={{
          background: '#090D13',
          borderBottom: '1px solid #182030',
          padding: '12px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Mail size={12} style={{ color: gc }} />
          <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.12em' }}>
            EMAIL SECURITY POSTURE
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: '#5A7A9A', fontSize: 9 }}>{gradeLabel(posture.grade)}</span>
          <div
            style={{
              width: 32, height: 32, borderRadius: '50%',
              background: `${gc}18`,
              border: `2px solid ${gc}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'monospace', fontWeight: 900, fontSize: 14, color: gc,
            }}
          >
            {posture.grade}
          </div>
        </div>
      </div>

      {/* Check rows */}
      <div style={{ padding: '0 16px' }}>
        <PostureRow
          ok={posture.spf_present}
          label="SPF record"
          detail={posture.spf_strict ? '-all / ~all enforced' : posture.spf_present ? 'permissive (+all)' : undefined}
        />
        <PostureRow
          ok={posture.dmarc_present}
          label="DMARC record"
          detail={posture.dmarc_policy ? `p=${posture.dmarc_policy}` : undefined}
        />
        <PostureRow
          ok={posture.dkim_selectors_found.length > 0}
          label="DKIM signing"
          detail={posture.dkim_selectors_found.length > 0 ? posture.dkim_selectors_found.slice(0, 3).join(', ') : undefined}
        />
        <PostureRow
          ok={posture.spf_strict && posture.dmarc_present && posture.dmarc_policy === 'reject'}
          label="Anti-spoofing enforcement"
        />
      </div>

      {/* DKIM selectors */}
      {posture.dkim_selectors_found.length > 0 && (
        <div style={{ borderTop: '1px solid #182030', padding: '10px 16px' }}>
          <p style={{ color: '#5A7A9A', fontSize: 8, fontFamily: 'monospace', letterSpacing: '0.1em', marginBottom: 6 }}>
            DKIM SELECTORS FOUND
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {posture.dkim_selectors_found.map(s => (
              <span
                key={s}
                style={{
                  background: '#0F1928', border: '1px solid #1E2B3F',
                  color: '#00C853', fontSize: 9, fontFamily: 'monospace',
                  padding: '2px 8px', borderRadius: 4,
                }}
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SubdomainList({ subdomains }: { subdomains: SubdomainEntry[] }) {
  const [expanded, setExpanded] = useState(false);
  const sensitive = subdomains.filter(s => s.is_sensitive);
  const shown = expanded ? subdomains : subdomains.slice(0, 12);

  return (
    <div
      style={{
        background: '#0C1118',
        border: `1px solid ${sensitive.length > 0 ? '#FF6B1A30' : '#182030'}`,
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          background: '#090D13',
          borderBottom: '1px solid #182030',
          padding: '12px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Globe size={12} style={{ color: '#2979FF' }} />
          <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.12em' }}>
            CERTIFICATE TRANSPARENCY — SUBDOMAIN EXPOSURE
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {sensitive.length > 0 && (
            <span
              style={{
                background: '#FF6B1A18', color: '#FF6B1A',
                border: '1px solid #FF6B1A30',
                fontSize: 8, fontFamily: 'monospace', fontWeight: 700,
                padding: '2px 8px', borderRadius: 99,
                display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <AlertTriangle size={8} /> {sensitive.length} SENSITIVE
            </span>
          )}
          <span
            style={{
              background: '#2979FF18', color: '#2979FF',
              border: '1px solid #2979FF30',
              fontSize: 8, fontFamily: 'monospace', fontWeight: 700,
              padding: '2px 8px', borderRadius: 99,
            }}
          >
            {subdomains.length} TOTAL
          </span>
        </div>
      </div>

      {/* List */}
      <div style={{ padding: '12px 16px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 4,
          }}
        >
          {shown.map((sub, i) => (
            <div
              key={i}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: sub.is_sensitive ? '#1A0D0018' : '#090D13',
                border: `1px solid ${sub.is_sensitive ? '#FF6B1A30' : '#182030'}`,
                borderRadius: 5,
                padding: '5px 8px',
              }}
            >
              {sub.is_sensitive && (
                <ShieldAlert size={9} style={{ color: '#FF6B1A', flexShrink: 0 }} />
              )}
              <span
                style={{
                  color: sub.is_sensitive ? '#FF6B1A' : '#8AA4BE',
                  fontSize: 9,
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {sub.subdomain}
              </span>
            </div>
          ))}
        </div>

        {subdomains.length > 12 && (
          <button
            onClick={() => setExpanded(e => !e)}
            style={{
              marginTop: 10,
              display: 'flex', alignItems: 'center', gap: 4,
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#2979FF', fontSize: 9, fontFamily: 'monospace',
            }}
          >
            {expanded ? (
              <><ChevronUp size={10} /> Show less</>
            ) : (
              <><ChevronDown size={10} /> Show all {subdomains.length} subdomains</>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Surface summary metrics ────────────────────────────────────────────────────

function SurfaceMetric({ label, value, color, sub }: {
  label: string; value: string | number; color: string; sub?: string;
}) {
  return (
    <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '12px 14px' }}>
      <div style={{ color, fontFamily: 'monospace', fontWeight: 800, fontSize: 22, lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.06em', marginTop: 4 }}>
        {label}
      </div>
      {sub && (
        <div style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', marginTop: 2 }}>{sub}</div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface AttackSurfaceProps {
  exposure: CompanyAttackSurface;
}

export function AttackSurface({ exposure }: AttackSurfaceProps) {
  const posture = exposure.email_posture;
  const gc = gradeColor(posture?.grade);

  return (
    <div className="flex flex-col gap-4">
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Lock size={12} style={{ color: '#FF6B1A' }} />
        <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em' }}>
          ATTACK SURFACE ANALYSIS — {exposure.domain}
        </span>
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-4 gap-3">
        <SurfaceMetric
          label="SUBDOMAINS"
          value={exposure.subdomain_count}
          color={exposure.subdomain_count > 20 ? '#FF6B1A' : '#2979FF'}
          sub="via crt.sh CT logs"
        />
        <SurfaceMetric
          label="SENSITIVE HOSTS"
          value={exposure.sensitive_subdomain_count}
          color={exposure.sensitive_subdomain_count > 0 ? '#FF6B1A' : '#5A7A9A'}
          sub="admin, vpn, dev, db…"
        />
        <SurfaceMetric
          label="EMAIL POSTURE"
          value={posture?.grade ?? 'N/A'}
          color={gc}
          sub={gradeLabel(posture?.grade)}
        />
        <SurfaceMetric
          label="DKIM SELECTORS"
          value={posture?.dkim_selectors_found.length ?? 0}
          color={posture?.dkim_selectors_found.length ? '#00C853' : '#FF1F1F'}
          sub="Signing keys found"
        />
      </div>

      {/* Email posture detail */}
      {posture && <EmailPostureCard posture={posture} />}

      {/* Subdomain list */}
      {exposure.subdomains.length > 0 && (
        <SubdomainList subdomains={exposure.subdomains} />
      )}
    </div>
  );
}
